import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../../api/client";
import type { ApiCtx } from "../../api/client";
import type { DocMeta, GetResponse } from "../../api/types";
import { Markdown } from "../../parse/markdown";
import { PythonCode } from "../../parse/pycode";
import { CsvTable } from "../../parse/csvtable";
import { GoalEditor } from "./GoalEditor";
import { downloadSingle } from "../../files/download";
import { texte } from "../../texte";
import { toast } from "../../notify";
import { useUi } from "../../state/ui";
import { useSettings } from "../../state/settings";

/** Dateivorschau im Dateien-Tab (Liste oder Rail-Tree, client.md §11).
 * „‹ Zur Übersicht“ kehrt zur Dateiliste zurück; die Tab-Leiste bleibt sichtbar. */
export function FileView({ ctx, docId, meta }: { ctx: ApiCtx; docId: string; meta?: DocMeta }) {
  const closeFile = useUi((s) => s.closeFile);
  const editIntent = useUi((s) => s.editIntent);
  const consumeEditIntent = useUi((s) => s.consumeEditIntent);
  const classic = useSettings((s) => s.classic);
  const queryClient = useQueryClient();
  const queryKey = ["doc", ctx.apiUrl, ctx.token, ctx.workspace, docId];
  const query = useQuery({ queryKey, queryFn: () => api.get(ctx, docId) });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const doc = query.data;
  const isImage = !!doc?.is_image && !!doc.content_base64;
  // `doc.filename` ist der server-aufgelöste Name (libs.memory._doc_filename) — verlässlicher
  // als docId, da z. B. ein doc_id ohne erlaubte Endung serverseitig auf .md fällt.
  const filename = (doc?.filename ?? docId).toLowerCase();
  const isPythonFile = filename.endsWith(".py");
  const isPython = !isImage && !!doc?.content && isPythonFile;
  const isCsv = !isImage && !!doc?.content && filename.endsWith(".csv");
  const isPlan = !isImage && !!doc?.content && filename.startsWith("plan");
  // agent_status ist werkzeuggepflegt (siehe PROTECTED_WRITE in agenttoolkit.py) und
  // .py-Skripte sind ein Protokoll dessen, was tatsächlich lief — beides soll nicht per
  // Hand überschreibbar sein, auch wenn andere Kontrolldateien (goal, manifest, …) es sind.
  const isEditable = !isImage && !isPythonFile && docId !== "agent_status";

  async function handleDownload() {
    try {
      await downloadSingle(ctx, docId);
      toast(texte.fileView.downloaded, texte.fileView.downloadedText, true);
    } catch (e) {
      toast(texte.errors.generic, e instanceof Error ? e.message : String(e));
    }
  }

  function handleEdit() {
    setDraft(doc?.content ?? "");
    setEditing(true);
  }

  // Sprung direkt in den Bearbeiten-Modus, ausgelöst z. B. vom Bearbeiten-Button auf der
  // Aufgaben-Karte im Verlauf-Tab (useUi.openFileForEdit) — wartet auf den geladenen Inhalt,
  // damit der Entwurf nicht mit einem leeren String startet.
  useEffect(() => {
    if (editIntent === docId && doc && isEditable) {
      handleEdit();
      consumeEditIntent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editIntent, docId, doc, isEditable]);

  function handleCancel() {
    setEditing(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const res = await api.add(ctx, [{ doc_id: docId, content: draft }]);
      const written = res.written?.[0];
      const writeError = res.code < 0 ? res.msg : written && "error" in written ? written.error : undefined;
      if (writeError) {
        toast(texte.errors.generic, writeError);
        return;
      }
      // Zwischenspeicher sofort mit dem geschriebenen Inhalt aktualisieren, statt nur zu
      // invalidieren: invalidateQueries löst den Refetch nur im Hintergrund aus, und ohne
      // diesen Schritt zeigt ein sofortiges erneutes „Bearbeiten“ noch den alten Stand
      // (handleEdit liest aus query.data) — der eben gespeicherte Text wäre scheinbar weg.
      queryClient.setQueryData<GetResponse>(queryKey, (old) => (old ? { ...old, content: draft } : old));
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["files", ctx.apiUrl, ctx.token, ctx.workspace] });
      setEditing(false);
      toast(texte.fileView.saved, texte.fileView.savedText, true);
    } catch (e) {
      toast(texte.errors.generic, e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="file-view-head">
        <button className="back" onClick={closeFile}>
          {texte.fileView.back}
        </button>
        <div className="titlewrap">
          <h2>📄 {docId}</h2>
          <div className="meta">{meta ? `${meta.summary} · ${meta.updated_at}` : ""}</div>
        </div>
        <div className="actions">
          {editing ? (
            <>
              <button className="btn ghost" disabled={saving} onClick={handleCancel}>
                {texte.fileView.cancel}
              </button>
              <button className="btn primary" disabled={saving} onClick={handleSave}>
                {saving ? texte.fileView.saving : texte.fileView.save}
              </button>
            </>
          ) : (
            <>
              {isEditable && (
                <button className="btn" onClick={handleEdit}>
                  {texte.fileView.edit}
                </button>
              )}
              <button className="btn" onClick={handleDownload}>
                {texte.fileView.download}
              </button>
            </>
          )}
        </div>
      </div>
      {editing ? (
        docId === "goal" ? (
          <GoalEditor value={draft} onChange={setDraft} />
        ) : (
          <textarea
            className="file-edit-area"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            autoFocus
          />
        )
      ) : isImage ? (
        <img
          src={`data:image/*;base64,${doc!.content_base64}`}
          alt={docId}
          style={{ maxWidth: "100%", borderRadius: 12 }}
        />
      ) : isPython ? (
        <PythonCode src={doc!.content!} />
      ) : isCsv ? (
        <CsvTable src={doc!.content!} />
      ) : isPlan && !classic ? (
        <pre style={{ whiteSpace: "pre-wrap", padding: 16, fontFamily: "monospace", margin: 0 }}>
          {doc!.content!}
        </pre>
      ) : (
        <Markdown src={doc?.content ?? texte.fileView.notAvailable} />
      )}
    </>
  );
}
